<script setup lang="ts">
import { debounce } from 'lodash-es';
import { computed, ref, toRef, useSlots } from 'vue';

import { KaDescription, KaLabel, KaMessage } from '@/kasten-components/components';
import type {
  KaVeeFormFieldEmitContract,
  KaVeeFormFieldPropsContract,
} from '@/kasten-components/components/KaVeeFormField';
import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import type { HTMLElementEvent } from '@/kasten-components/types/html-element-types';
import { cnMerge } from '@/utils/utils.js';
import IconEyeOff from '~icons/ion/eye-off-outline';
import IconEye from '~icons/ion/eye-outline';

export type KaInputType =
  'text' | 'email' | 'url' | 'password' | 'tel' | 'datetime-local' | 'date' | 'time';

export type KaInputProps = KaVeeFormFieldPropsContract<string> & {
  type?: KaInputType;
  modelValue?: string;
  name: string;
  label: string;
  hideLabel?: boolean;
  description?: string;
  hideDescription?: boolean;
  id?: string;
  dataTestid?: string;
  errorMessage?: string;
  /** Used to link an error message element, handled outside of this component, to this component accessibly. This should not be used if the error message is provided via the `errorMessage` prop.
   * This is useful when you want to share the same error message for multiple inputs, such as in a fieldset.
   * The error message element should have an `id` that matches this value.
   * This will also be used to set the `aria-errormessage` attribute on the input element.
   * If not provided, a fallback ID will be generated based on the input's ID.
   * @default undefined
   */
  errorMessageId?: string;
  /** Whether to visually hide the error message so that it is still present in the DOM and only visible to screen-readers. */
  hideErrorMessage?: boolean;
  /** Remove the error message from the DOM completely. This will keep aria invalid properties and error styling, but allow you to put your own error message in the DOM.
   * This is most likely used when you use this input in a fieldset with other inputs that share the same error message and you want to see the error styling but tie the error message to the fieldset instead of the input.
   * @default false
   */
  showErrorStylesOnly?: boolean;
  debounceMs?: number;
  containerClasses?: VueClass;
  inputContainerClasses?: VueClass;
  inputClasses?: VueClass;
  width?: 'full' | 'auto';
  required?: boolean;
  ariaRequired?: boolean;
  /** Only use this property if you plan to add the asterisk in the slot content of the label.
   * A required field MUST have a visual indicator to be accessible.
   *
   * https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA2
   *
   * https://veeam.hub.essentia11y.com/manual-evaluations/67af37db16faa72414aa1705/results/AMP256/view?skipScanStandards=true&evaluationStandard=WCAG_v22_AAA&status=toReview,open,partiallyFixed,notFixed,cannotBeFixed,validationRequested&issueId=AMP256&linkedPropertyData=6762fe26f5d3a5eaed29f2c8%7C6723d15450ee91f82f056d41
   */
  hideRequiredAsterisk?: boolean;
};

type KaInputEmits = KaVeeFormFieldEmitContract<HTMLElementEvent<HTMLInputElement>> & {
  (e: 'update:modelValue', value: string): void;
  (e: 'input', event: HTMLElementEvent<HTMLInputElement>): void;
  (e: 'change', event: HTMLElementEvent<HTMLInputElement>): void;
  (e: 'blur', event: FocusEvent): void;
};

defineOptions({
  name: 'KaInput',
  inheritAttrs: false,
});

const props = withDefaults(defineProps<KaInputProps>(), {
  type: 'text',
  mode: 'eager',
  modelValue: '',
  width: 'full',
  showErrorStylesOnly: false,
  required: false,
  ariaRequired: false,
  hideRequiredAsterisk: false,
});

const emit = defineEmits<KaInputEmits>();
const slots = useSlots();

defineSlots<{
  label?(props: { label: string }): any;
  description?(props: { description: string | undefined }): any;
  left?(props: Record<string, never>): any;
  right?(props: Record<string, never>): any;
}>();

const showPassword = ref(false);

const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const name = toRef(props, 'name');
const id = computed(() => props.id ?? name.value);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const labelId = computed(() => `${id.value}-label`);
const descriptionId = computed(() =>
  props.description || slots.description ? `${id.value}-description` : undefined,
);
const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const hasError = computed(() => props.errorMessage !== undefined);
const isPassword = computed(() => props.type === 'password');
const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);
const widthClass = computed(() => (props.width === 'full' ? 'w-full' : 'w-auto'));

const emitInputDebounced = debounce((event: HTMLElementEvent<HTMLInputElement>) => {
  emit('input', event);
}, props.debounceMs);

const handleInput = (payload: Event) => {
  const event = payload as HTMLElementEvent<HTMLInputElement>;

  if (props.debounceMs) {
    emitInputDebounced(event);
  } else {
    emit('input', event);
  }
};

const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});

const isRequired = computed(() => props.required || props.ariaRequired);
</script>

<template>
  <div :class="cnMerge('w-full', props.containerClasses)">
    <KaLabel
      :id="labelId"
      :data-testid="suffix('label')"
      :for="id"
      :hide-label="props.hideLabel"
      :class="!shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
      :required="isRequired"
      :hide-required-asterisk="props.hideRequiredAsterisk"
    >
      <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
    </KaLabel>

    <KaDescription
      v-if="props.description || $slots.description"
      :id="descriptionId"
      :data-testid="suffix('description')"
      :description="props.description"
      :hide-description="props.hideDescription"
      :class="shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
    >
      <slot name="description" v-bind="{ description: props.description }" />
    </KaDescription>

    <div :class="cnMerge('relative', widthClass, props.inputContainerClasses)">
      <div
        v-if="$slots['left']"
        class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
        :data-testid="suffix('left')"
      >
        <slot name="left" />
      </div>

      <input
        v-bind="$attrs"
        :id="id"
        v-model="internalValue"
        :data-testid="dataTestid"
        :name="name"
        :type="isPassword && showPassword ? 'text' : props.type"
        class="form-input block rounded border-0 py-1.5 text-sm leading-6 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset dark:bg-navy-800"
        :class="
          cnMerge([
            widthClass,
            props.showErrorStylesOnly || hasError
              ? 'text-red-600 ring-red-500/60 placeholder:text-red-500 focus:ring-red-500/80 dark:text-red-400'
              : 'text-gray-900 ring-gray-300 placeholder:text-gray-600 focus:ring-blue-400 dark:text-gray-50 dark:ring-navy-600 dark:placeholder:text-gray-400 dark:focus:ring-blue-500',
            (isPassword || $slots['right']) && 'pr-10',
            $slots['left'] && 'pl-10',
            props.inputClasses,
          ])
        "
        :aria-describedby="descriptionId"
        :aria-invalid="props.showErrorStylesOnly || hasError"
        :aria-errormessage="computedErrorMessageId"
        :aria-required="isRequired"
        @change="emit('change', $event as HTMLElementEvent<HTMLInputElement>)"
        @input="handleInput"
        @blur="emit('blur', $event)"
      />
      <div
        v-if="$slots['right']"
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
        :data-testid="suffix('right')"
      >
        <slot name="right" />
      </div>
      <!-- Show/Hide Password Button -->
      <button
        v-if="isPassword"
        type="button"
        :aria-pressed="showPassword"
        class="absolute inset-y-0 right-0 mr-2 flex items-center px-1"
        :data-testid="suffix('type-toggle')"
        @click="showPassword = !showPassword"
      >
        <IconEyeOff v-if="showPassword" class="h-5 w-5" aria-hidden="true" />
        <IconEye v-else class="h-5 w-5" aria-hidden="true" />
        <span class="sr-only">{{ showPassword ? 'Hide' : 'Show' }} {{ props.label }}</span>
      </button>
    </div>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
    />
  </div>
</template>
